import { useCallback, useEffect, useState } from "react";
import {
  createMerchantCategory,
  deleteMerchantCategory,
  getMerchantCategories,
  reorderMerchantCategories,
  updateMerchantCategory,
} from "../api/merchantCategory.api";
import type { MerchantCategory, MerchantCategoryPayload } from "../types/merchantCategory.types";

export function useMerchantCategories() {
  const [items, setItems] = useState<MerchantCategory[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getMerchantCategories({
        page,
        page_size: 20,
        keyword: keyword || undefined,
      });
      setItems(response.list);
      setTotal(response.paging.total);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Failed to load merchant categories",
      );
    } finally {
      setIsLoading(false);
    }
  }, [keyword, page]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const save = async (id: string | undefined, payload: MerchantCategoryPayload) => {
    if (id) await updateMerchantCategory(id, payload);
    else await createMerchantCategory(payload);
    await refetch();
  };

  const remove = async (id: string) => {
    await deleteMerchantCategory(id);
    await refetch();
  };

  const reorder = async (orderedIds: string[]) => {
    await reorderMerchantCategories(orderedIds);
    await refetch();
  };

  return {
    items,
    page,
    total,
    keyword,
    isLoading,
    error,
    setPage,
    setKeyword,
    refetch,
    save,
    remove,
    reorder,
  };
}
